const TELEMETRY_ENDPOINT = (import.meta.env.VITE_TELEMETRY_URL as string | undefined)
  ?? (import.meta.env.DEV
    ? '/telemetry-proxy/v1/analytics/events'
    : 'https://telemetry-api-proxy.chargers-telemetry-pro.wall-box.com/v1/analytics/events')

const DEVICE_ID_KEY = 'telemetry_device_id'
const SESSION_INACTIVITY_MS = 30 * 60 * 1000

const SESSION_KEY = 'telemetry_session'

// In-memory copy, used as fallback when storage is unavailable
let sessionId = ''
let lastActivityMs = 0

function getDeviceId (): string {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(DEVICE_ID_KEY, id)
  }
  return id
}

function readStoredSession (): { id: string; lastActivityMs: number } | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { id?: unknown; lastActivityMs?: unknown }
    if (typeof parsed.id === 'string' && typeof parsed.lastActivityMs === 'number') {
      return { id: parsed.id, lastActivityMs: parsed.lastActivityMs }
    }
  } catch {
    // storage unavailable or corrupted, fall back to in-memory state
  }
  return null
}

// Session survives page refreshes and is shared across tabs; it expires after
// SESSION_INACTIVITY_MS without any tracked activity.
function getSessionId (): string {
  const now = Date.now()
  const stored = readStoredSession()
  if (stored) {
    sessionId = stored.id
    lastActivityMs = stored.lastActivityMs
  }
  if (!sessionId || now - lastActivityMs > SESSION_INACTIVITY_MS) {
    sessionId = crypto.randomUUID()
  }
  lastActivityMs = now
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ id: sessionId, lastActivityMs }))
  } catch {
    // best-effort persistence
  }
  return sessionId
}

function getBrowserInfo (): { name: string; version: string } {
  const ua = navigator.userAgent
  const browsers = [
    { name: 'Chrome', re: /Chrome\/(\S+)/ },
    { name: 'Firefox', re: /Firefox\/(\S+)/ },
    { name: 'Safari', re: /Version\/(\S+).*Safari/ },
    { name: 'Edge', re: /Edg\/(\S+)/ },
  ] as const
  for (const { name, re } of browsers) {
    const m = ua.match(re)
    if (m) return { name, version: m[1].split('.')[0] }
  }
  return { name: 'Unknown', version: '0' }
}

export function telemetryTrack (
  eventName: string,
  extraProperties?: Record<string, unknown>
): void {
  const browser = getBrowserInfo()
  const userId = (extraProperties?.user_id ?? null) as string | null
  const payload = {
    event_id: crypto.randomUUID(),
    event_name: eventName,
    user_id: userId,
    device_id: getDeviceId(),
    timestamp_ms: Date.now(),
    properties: {
      $os: 'web',
      $os_version: browser.version,
      $app_version_string: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '',
      $app_build_number: (import.meta.env.VITE_APP_BUILD_NUMBER as string | undefined) ?? '',
      $manufacturer: browser.name,
      $model: `${browser.name} ${browser.version}`,
      $screen_width: window.screen.width,
      $screen_height: window.screen.height,
      $wifi: navigator.onLine,
      $session_id: getSessionId(),
      $brand: 'portal',
      ...extraProperties,
    },
  }

  fetch(TELEMETRY_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events: [payload] }),
    keepalive: true,
  }).catch(() => {
    // best-effort, ignore failures
  })
}
